import React from 'react'
import { Card, CardContent, Typography, Box, Chip, Avatar } from '@mui/material'
import { TrendingUp, TrendingDown } from '@mui/icons-material'
import { gradients, shadowColors, GradientType } from '../../theme/gradients'

interface GradientMetricCardProps {
  title: string
  value: string | number
  icon: React.ReactElement
  gradient: GradientType
  trend?: number
  subtitle?: string
  loading?: boolean
}

export const GradientMetricCard: React.FC<GradientMetricCardProps> = ({
  title,
  value,
  icon,
  gradient,
  trend,
  subtitle,
  loading = false,
}) => {
  return (
    <Card
      sx={{
        background: gradients[gradient],
        color: 'white',
        borderRadius: 4,
        height: '100%',
        minHeight: 180,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: `0 8px 24px ${shadowColors[gradient]}`,
        transition: 'all 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: `0 16px 48px ${shadowColors[gradient]}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)',
          opacity: 0,
          transition: 'opacity 0.3s ease',
        },
        '&:hover::before': {
          opacity: 1,
        },
      }}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Icon and Value Section */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.9,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                lineHeight: 1.2,
              }}
            >
              {loading ? '...' : value}
            </Typography>
          </Box>
          
          <Avatar
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.25)',
              width: { xs: 48, sm: 56 },
              height: { xs: 48, sm: 56 },
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'rotate(360deg) scale(1.1)',
              },
            }}
          >
            {React.cloneElement(icon, {
              sx: { fontSize: { xs: 24, sm: 28 } }
            })}
          </Avatar>
        </Box>

        {/* Trend and Subtitle Section */}
        <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                opacity: 0.9,
                fontSize: '0.875rem',
              }}
            >
              {subtitle}
            </Typography>
          )}
          
          {trend !== undefined && (
            <Chip
              icon={trend >= 0 ? <TrendingUp /> : <TrendingDown />}
              label={`${trend >= 0 ? '+' : ''}${trend}%`}
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 600,
                backdropFilter: 'blur(10px)',
                '& .MuiChip-icon': {
                  color: 'white',
                },
              }}
            />
          )}
        </Box>

        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 150,
            height: 150,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            filter: 'blur(60px)',
            animation: 'float 6s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translate(0, 0)' },
              '50%': { transform: 'translate(-20px, -20px)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 100,
            height: 100,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            filter: 'blur(40px)',
            animation: 'float 8s ease-in-out infinite',
            animationDelay: '1s',
          }}
        />
      </CardContent>
    </Card>
  )
}
